import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '@/lib/utils';

const API = getApiUrl();

export const OtherIncomePage = ({ user, t, formatCurrency, buildingId = null }) => {
  const navigate = useNavigate();
  const [incomes, setIncomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingIncome, setEditingIncome] = useState(null);
  const [formData, setFormData] = useState({
    category: 'commonAreaRental',
    description: '',
    amount: '',
    date: new Date().toISOString().split('T')[0]
  });

  const fetchIncomes = useCallback(async () => {
    try {
      const url = buildingId ? `${API}/other-income?building_id=${buildingId}` : `${API}/other-income`;
      const response = await axios.get(url, { withCredentials: true });
      setIncomes(response.data);
    } catch (error) {
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  }, [buildingId, t]);

  useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'superadmin') {
      navigate('/app');
      return;
    }
    fetchIncomes();
  }, [user, navigate, fetchIncomes]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        amount: parseFloat(formData.amount)
      };

      if (editingIncome) {
        const url = buildingId 
          ? `${API}/other-income/${editingIncome.id}?building_id=${buildingId}`
          : `${API}/other-income/${editingIncome.id}`;
        await axios.put(url, payload, { withCredentials: true });
        toast.success(t('otherIncomeUpdated'));
      } else {
        const url = buildingId ? `${API}/other-income?building_id=${buildingId}` : `${API}/other-income`;
        await axios.post(url, payload, { withCredentials: true });
        toast.success(t('otherIncomeCreated'));
      }

      setShowModal(false);
      setEditingIncome(null);
      setFormData({
        category: 'commonAreaRental',
        description: '',
        amount: '',
        date: new Date().toISOString().split('T')[0]
      });
      fetchIncomes();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  const handleEdit = (income) => {
    setEditingIncome(income);
    setFormData({
      category: income.category,
      description: income.description,
      amount: income.amount.toString(),
      date: income.date
    });
    setShowModal(true);
  };

  const handleDelete = async (incomeId) => {
    if (!window.confirm(t('confirmDeleteOtherIncome'))) return;

    try {
      const url = buildingId 
        ? `${API}/other-income/${incomeId}?building_id=${buildingId}`
        : `${API}/other-income/${incomeId}`;
      await axios.delete(url, { withCredentials: true });
      toast.success(t('otherIncomeDeleted'));
      fetchIncomes();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('operationFailed'));
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingIncome(null);
    setFormData({
      category: 'commonAreaRental',
      description: '',
      amount: '',
      date: new Date().toISOString().split('T')[0]
    });
  };

  const getCategoryLabel = (category) => {
    const categoryMap = {
      commonAreaRental: t('commonAreaRental'),
      parkingFees: t('parkingFees'),
      facilityUsageFees: t('facilityUsageFees'),
      penalties: t('penalties'),
      keyCardFees: t('keyCardFees'),
      otherCategory: t('otherCategory')
    };
    return categoryMap[category] || category;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-blue-50 to-indigo-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/app')} className="text-gray-600 hover:text-gray-900">
                ← {t('back')}
              </button>
              <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {t('otherIncomeTitle')}
              </h1>
            </div>
            <button
              onClick={() => setShowModal(true)}
              className="px-4 py-2 bg-teal-600 text-white rounded-full hover:bg-teal-700 transition-colors"
            >
              {t('newOtherIncome')}
            </button>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('date')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('category')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('description')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('amount')}</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase">{t('actions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {incomes.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      {t('noDataYet')}
                    </td>
                  </tr>
                ) : (
                  incomes.map((income) => (
                    <tr key={income.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm">
                        {new Date(income.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                          {getCategoryLabel(income.category)}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm">{income.description}</td>
                      <td className="px-6 py-4 text-sm font-semibold text-green-600">
                        {formatCurrency(income.amount)}
                      </td>
                      <td className="px-6 py-4 text-sm">
                        <div className="flex gap-3">
                          <button
                            onClick={() => handleEdit(income)}
                            className="text-blue-600 hover:text-blue-700 font-semibold"
                          >
                            {t('edit')}
                          </button>
                          <button
                            onClick={() => handleDelete(income.id)}
                            className="text-red-600 hover:text-red-700 font-semibold"
                          >
                            {t('delete')}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {editingIncome ? t('editOtherIncome') : t('newOtherIncome')}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('date')}</label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('category')}</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                >
                  <option value="commonAreaRental">{t('commonAreaRental')}</option>
                  <option value="parkingFees">{t('parkingFees')}</option>
                  <option value="facilityUsageFees">{t('facilityUsageFees')}</option>
                  <option value="penalties">{t('penalties')}</option>
                  <option value="keyCardFees">{t('keyCardFees')}</option>
                  <option value="otherCategory">{t('otherCategory')}</option>
                </select>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('description')}</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows="3"
                  required
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">{t('amount')}</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  {t('cancel')}
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                >
                  {editingIncome ? t('save') : t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
